import {
  DomainTuple,
  VictoryAxis,
  VictoryCandlestick,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import { Candle } from '../../../../../Detail/types';
import {
  CompactTransaction,
  IndicatorDataPoint,
  SavedData,
} from '../../../../types';
import './index.css';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { colors } from '../../../../../../colors';
import { createXTickValues } from '../../../../../../helpers/bundleTransactionsToCompactForm';
import {
  createYAxisCandlesticksTicks,
  niceLinearTicks,
  OverlayLine,
} from '../../../../../../helpers/Chart';
import moment from 'moment';
import { getFormatUnit } from '../../../../../../helpers/bundleTransactionsToCompactForm';
import { formatYTick } from '../../../../../../helpers/Chart';
import { ComponentDimensions } from '../../../../../../types/Layout';
import {
  formatBalance,
  formatDate,
  formatPercentage,
  formatPrice,
} from '../../../../../../helpers/NumberFormatter';
import ChartTooltip, {
  TOOLTIP_WIDTH,
  TooltipModel,
  tooltipModelHeight,
} from '../Charts/CustomTooltip';
import MultiChart, {
  MultiChartCursorContext,
  XAxisInfo,
} from '../../../../../../components/MultiChart';
import DropdownComponent from '../../../../../../components/Form/Dropdown';
import { Exchange } from 'grts-utils/types/Exchange';
import DomainXZoomer from '../../../../../../components/Charts/DomainXZoomer/DomainXZoomer';

interface CandleChartProps {
  candles: Candle[];
  compactTransactions: CompactTransaction[];
  signals: IndicatorDataPoint[];
  rootDimensions: ComponentDimensions;
  setXAxisInfo?:
    | React.Dispatch<React.SetStateAction<XAxisInfo | undefined>>
    | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  /** Hide both axes + grid; use full container height. */
  headless?: boolean;
  /** First child of a MultiChart group: keep the y-axis, drop the x-axis
   * (the shared x-axis is drawn by the last child) and use full height. */
  sharedXAxis?: boolean;
  /** Render evenly spaced, round-number y-axis ticks instead of the
   * transaction/extreme-driven ones. */
  niceYTicks?: boolean;
  /** Horizontal annotation lines (cbrt ranges / MRC phases) over the candles. */
  overlayLines?: OverlayLine[];
}

/** Fully transparent axes — provided to a headless chart so Victory does not
 * inject its own default (visible) axes. */
const hiddenAxisStyle = {
  axis: { stroke: 'transparent' },
  ticks: { stroke: 'transparent', size: 0 },
  tickLabels: { fill: 'transparent' },
  grid: { stroke: 'transparent' },
};

const CandleChart = (props: CandleChartProps) => {
  const {
    candles,
    compactTransactions,
    signals,
    rootDimensions,
    setXAxisInfo,
    xAxisInfo,
    headless,
    sharedXAxis,
    niceYTicks,
    overlayLines,
  } = props;

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      // ticks: setXAxisInfo ? {} : { stroke: 'white', size: 2 },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 13, padding: 5, fill: 'gray' },
    }),
    [],
  );

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 20, padding: 5, fill: '#999' },
    }),
    [],
  );

  const data = useMemo(() => {
    return candles
      .filter(
        (c) =>
          c.startTimestamp >= (xAxisInfo?.domain.x[0] ?? 0) &&
          c.endTimestamp <= (xAxisInfo?.domain.x[1] ?? Infinity),
      )
      .map((c) => ({
        x: c.startTimestamp,
        x2: c.endTimestamp,
        high: c.high,
        close: c.close,
        open: c.open,
        low: c.low,
      }));
  }, [candles, xAxisInfo?.domain.x]);

  const domain = useMemo(() => {
    if (!data?.length) {
      return undefined;
    }

    // Overlay lines (cbrt / mrc) can sit outside the candle range — widen the
    // y-domain so they stay visible.
    const overlayYs = (overlayLines ?? []).flatMap((l) =>
      l.points.map((p) => p.y),
    );
    const minY = Math.min(...data.map((c) => c.low), ...overlayYs);
    const maxY = Math.max(...data.map((c) => c.high), ...overlayYs);

    return {
      x: [data[0].x, data[data.length - 1].x] as DomainTuple,
      y: [minY, maxY] as DomainTuple,
    };
  }, [data, overlayLines]);

  const transactionsDataPoints = useMemo(() => {
    return compactTransactions.map((t) => {
      const type = t.transactionType ?? '';
      const isStopLoss = type.includes('STOP_LOSS');
      const isTakeProfit = type.includes('TAKE_PROFIT') && !isStopLoss;
      const isBuy = t.orderType === 'BUY';
      // Core = white; take-profit = yellow; stop-loss = blue.
      const accent = isStopLoss
        ? colors.blue
        : isTakeProfit
        ? colors.yellow
        : colors.white;
      // Profit is realised in the asset for a BUY, in stable for a SELL.
      const profitValue = isBuy ? t.profitInAsset : t.profitInUsdt;

      const tooltip: TooltipModel = {
        title: isStopLoss
          ? 'STOP LOSS'
          : isTakeProfit
          ? 'TAKE PROFIT'
          : `CORE ${t.orderType}`,
        accent,
        badge: `#${t.id}`,
        rows: [
          { label: 'Date', value: formatDate(t.timestamp) },
          { label: 'Price', value: formatPrice(t.orderPrice, true, 2) },
          {
            label: isBuy ? 'Asset profit' : 'Stable profit',
            value: formatPercentage(profitValue),
            tone: profitValue < 0 ? 'neg' : profitValue > 0 ? 'pos' : undefined,
          },
          { label: 'Balance', value: formatBalance(t.totalBalanceValue) },
        ],
      };

      return {
        y: t.orderPrice,
        x: t.timestamp,
        color: t.orderType === 'SELL' ? colors.red : colors.green,
        strokeColor: accent,
        // Take-profit / stop-loss dots are a little smaller than core dots.
        size: isStopLoss || isTakeProfit ? 3.5 : 5,
        tooltip,
      };
    });
  }, [compactTransactions]);

  const signalDataPoints = useMemo(() => {
    return signals?.map((s) => {
      const formattedDate = formatDate(s.timestamp);
      const label = `${formattedDate}\nValue: ${s.value}\nPrice: ${formatPrice(
        s.price,
        true,
        2,
      )}`;

      return {
        y: s.price,
        x: s.timestamp,
        label,
        color: s.signalType === 'SELL' ? colors.red : colors.green,
      };
    });
  }, [signals]);

  const tickValues = useMemo(() => {
    if (!domain?.x) {
      return [];
    }

    return createXTickValues(domain.x[0] as number, domain.x[1] as number);
  }, [domain?.x]);

  const niceY = useMemo(
    () =>
      niceYTicks && domain
        ? niceLinearTicks(domain.y[0] as number, domain.y[1] as number)
        : null,
    [niceYTicks, domain],
  );

  const yTickValues = useMemo(() => {
    if (!domain) {
      return [];
    }

    if (niceY && niceY.values.length > 0) {
      return niceY.values;
    }

    return createYAxisCandlesticksTicks(
      data,
      compactTransactions,
      undefined,
      domain,
    );
  }, [niceY, compactTransactions, data, domain]);

  const yTickFormat = useMemo(() => {
    if (!niceY || niceY.step <= 0) {
      return formatYTick;
    }
    const decimals = Math.max(0, -Math.floor(Math.log10(niceY.step)));
    return (t: number) => formatPrice(t, true, decimals, true);
  }, [niceY]);

  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const getCandleStrokeColor = useCallback((c: any) => {
    return c.datum.close >= c.datum.open ? 'green' : 'red';
  }, []);

  const paddingRight = useMemo(() => {
    if (!yTickValues?.length) {
      return 0;
    }

    const longestTick = yTickValues
      .sort((a, b) => b.toString().length - a.toString().length)[0]
      .toString();

    const length = longestTick.length;
    const hasDecimal = longestTick.includes(',') || longestTick.includes('.');
    return 45 + length * 10 - (hasDecimal ? 5 : 0);
  }, [yTickValues]);

  const chartPadding = useMemo(
    () => ({
      left: 0,
      right: headless ? 0 : paddingRight,
      bottom: headless || sharedXAxis ? 0 : 20,
      top: headless ? 0 : sharedXAxis ? 8 : 30,
    }),
    [paddingRight, headless, sharedXAxis],
  );

  useEffect(() => {
    if (!domain?.x || !tickValues?.length || !!xAxisInfo) {
      return;
    }

    setXAxisInfo?.({
      tickValues,
      axisStyle,
      tickFormat: calculateTickValue,
      domain: { x: domain.x as [number, number] },
      domainPadding: { x: 30 },
      chartPadding,
    });
  }, [
    axisStyle,
    calculateTickValue,
    chartPadding,
    domain?.x,
    setXAxisInfo,
    tickValues,
    xAxisInfo,
  ]);

  // Hover crosshair — the candle under the shared MultiChart cursor: a dot at
  // its close, and its OHLC reported to the combined tooltip.
  const { cursorTimestamp, report } = useContext(MultiChartCursorContext);

  const cursorCandle = useMemo(() => {
    if (cursorTimestamp == null || !data.length) {
      return null;
    }
    return (
      data.find((c) => cursorTimestamp >= c.x && cursorTimestamp <= c.x2) ??
      data.reduce((best, c) =>
        Math.abs((c.x + c.x2) / 2 - cursorTimestamp) <
        Math.abs((best.x + best.x2) / 2 - cursorTimestamp)
          ? c
          : best,
      )
    );
  }, [cursorTimestamp, data]);

  const cursorDotData = useMemo(
    () =>
      cursorCandle && cursorTimestamp != null
        ? [{ x: cursorTimestamp, y: cursorCandle.close }]
        : [],
    [cursorCandle, cursorTimestamp],
  );

  useEffect(() => {
    if (!cursorCandle) {
      report('candle', []);
      return;
    }
    report('candle', [
      { label: 'Open', value: formatPrice(cursorCandle.open, true, 2) },
      { label: 'High', value: formatPrice(cursorCandle.high, true, 2) },
      { label: 'Low', value: formatPrice(cursorCandle.low, true, 2) },
      {
        label: 'Close',
        value: formatPrice(cursorCandle.close, true, 2),
        tone: cursorCandle.close >= cursorCandle.open ? 'pos' : 'neg',
      },
    ]);
  }, [cursorCandle, report]);

  return (
    <VictoryChart
      scale={{ x: 'time', y: 'log' }}
      domain={domain}
      width={rootDimensions?.width}
      height={
        headless || sharedXAxis
          ? rootDimensions?.height
          : rootDimensions?.height * 0.65
      }
      padding={chartPadding}
      domainPadding={{ x: headless ? 4 : 30 }}
    >
      {!headless && (
        <VictoryAxis
          style={sharedXAxis ? { ...axisStyle, ticks: {} } : axisStyle}
          tickFormat={sharedXAxis ? () => '' : calculateTickValue}
          tickValues={tickValues}
        />
      )}
      {!headless && (
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={yTickFormat}
          orientation={'right'}
          tickValues={yTickValues}
          tickLabelComponent={<VictoryLabel dx={5} />}
        />
      )}
      {/* Headless charts still need explicit axes, otherwise Victory injects
       * its own visible default axes. Render transparent ones instead. */}
      {headless && (
        <VictoryAxis style={hiddenAxisStyle} tickFormat={() => ''} />
      )}
      {headless && (
        <VictoryAxis
          dependentAxis
          style={hiddenAxisStyle}
          tickFormat={() => ''}
        />
      )}
      <VictoryCandlestick
        candleColors={{ positive: 'green', negative: 'red' }}
        data={data}
        style={{
          data: {
            fill: getCandleStrokeColor,
            stroke: getCandleStrokeColor,
            strokeWidth: 0.5,
          },
        }}
      />
      {(overlayLines ?? []).map((line, i) => (
        <VictoryLine
          key={`overlay_${i}`}
          data={line.points.map((p, idx) => ({
            x: p.x,
            y: p.y,
            label: idx === 0 ? line.label : undefined,
          }))}
          style={{
            data: { stroke: line.color, strokeWidth: line.strokeWidth },
          }}
          labelComponent={
            <VictoryLabel
              textAnchor="start"
              dy={line.labelDy}
              style={{ fill: line.color, fontSize: 8, fontWeight: 600 }}
              backgroundStyle={{ fill: 'rgba(0,0,0,0.55)' }}
              backgroundPadding={{ top: 1, bottom: 1, left: 2, right: 3 }}
            />
          }
        />
      ))}
      {/* Cursor dot sits behind the transaction dots so they stay hoverable. */}
      <VictoryScatter
        data={cursorDotData}
        size={4}
        style={{
          data: {
            fill:
              cursorCandle && cursorCandle.close >= cursorCandle.open
                ? 'green'
                : 'red',
            stroke: 'white',
            strokeWidth: 1.5,
            pointerEvents: 'none',
          },
        }}
      />
      <VictoryScatter
        data={transactionsDataPoints}
        style={{
          data: {
            fill: ({ datum }) => datum.color,
            stroke: ({ datum }) => datum.strokeColor,
            strokeWidth: 1.5,
          },
        }}
        size={({ datum }) => datum.size}
        labels={({ datum }) => (datum?.tooltip ? ' ' : '')}
        labelComponent={
          <VictoryTooltip
            constrainToVisibleArea
            pointerLength={7}
            flyoutWidth={TOOLTIP_WIDTH}
            flyoutHeight={(p: any) => tooltipModelHeight(p?.datum?.tooltip)}
            flyoutComponent={<ChartTooltip />}
            labelComponent={<g />}
          />
        }
      />
      <VictoryScatter
        data={signalDataPoints}
        style={{
          data: {
            fill: ({ datum }) => datum.color,
            stroke: colors.yellow,
            strokeWidth: 2,
          },
        }}
        size={5}
        labelComponent={
          <VictoryTooltip
            constrainToVisibleArea
            cornerRadius={3}
            orientation={'left'}
            centerOffset={{ x: -10, y: -40 }}
            pointerOrientation="right"
            pointerWidth={20}
            flyoutStyle={{
              stroke: (d) => d.datum.color,
              strokeWidth: 1.5,
              fill: 'rgba(255, 255, 255, 0.8)',
            }}
            flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
            labelComponent={
              <VictoryLabel
                textAnchor="start"
                dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                style={[
                  {
                    fill: 'rgb(0, 0, 0)',
                    fontFamily: 'monospace',
                    fontSize: 10,
                    fontWeight: 800,
                  },
                ]}
              />
            }
          />
        }
      />
    </VictoryChart>
  );
};

interface ProfitCalculationsResultCandleChartProps {
  savedData: SavedData;
  compactTransactions: CompactTransaction[];
  signals: IndicatorDataPoint[];
  rootDimensions: ComponentDimensions;
  timeFrames: {
    higherT: string;
    takeProfitT: string;
    shorterT: string;
    multiT: string;
  };
  indicatorConfig?: {
    rsi: Record<
      string,
      {
        period: number;
        threshold: number;
      }
    >;
  };
  baseAsset: string;
  quoteAsset: string;
  exchange: Exchange;
}

const ProfitCalculationsResultCandleChart = ({
  savedData,
  timeFrames,
  ...props
}: ProfitCalculationsResultCandleChartProps) => {
  const {
    higherTHeikinCandles,
    higherTNormalCandles,
    takeProfitTCandles,
    shorterTNormalCandles,
    multiOrderCandles,
  } = savedData;

  const initialXDomain: [number, number] = useMemo(() => {
    return [
      higherTNormalCandles[0].startTimestamp,
      higherTNormalCandles[higherTNormalCandles.length - 1].endTimestamp,
    ];
  }, [higherTNormalCandles]);

  const { rootDimensions } = props;
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(
    timeFrames.higherT,
  );

  const timeFrameOptions = [
    timeFrames.higherT,
    timeFrames.takeProfitT,
    timeFrames.shorterT,
    timeFrames.multiT,
  ];

  const candles = useMemo(() => {
    switch (selectedTimeFrame) {
      case timeFrames.higherT:
        return higherTNormalCandles;

      case timeFrames.takeProfitT:
        return takeProfitTCandles;

      case timeFrames.shorterT:
        return shorterTNormalCandles.flat();

      case timeFrames.multiT:
        return multiOrderCandles.flat(2);
    }

    return [];
  }, [
    higherTNormalCandles,
    multiOrderCandles,
    selectedTimeFrame,
    shorterTNormalCandles,
    takeProfitTCandles,
    timeFrames.higherT,
    timeFrames.multiT,
    timeFrames.shorterT,
    timeFrames.takeProfitT,
  ]);

  return (
    <div className={'profit-calcs-result-candlesticks-container'}>
      <h3 style={{ marginTop: 50, color: 'white', marginLeft: 50 }}>
        Strongest signals:
      </h3>
      <div style={{ width: 200, marginLeft: 50 }}>
        <DropdownComponent
          config={{
            label: 'TimeFrame',
            value: selectedTimeFrame,
            handleValueChange: setSelectedTimeFrame,
            options: timeFrameOptions,
            fullWidth: false,
            shouldFit: false,
          }}
        />
      </div>

      <div style={{ marginLeft: 50 }}>
        <MultiChart dimensions={rootDimensions}>
          <CandleChart {...props} candles={candles} />
          <DomainXZoomer
            originalXDomain={initialXDomain}
            rootDimensions={rootDimensions}
            data={higherTHeikinCandles.map((c) => ({
              x: c.endTimestamp,
              y: c.close,
            }))}
          />
        </MultiChart>
      </div>
    </div>
  );
};

export { CandleChart };
export default ProfitCalculationsResultCandleChart;

/**
 * @TODO
 *    Fix showing the signals (do we use all the signals we have? not always, depends on certain config like deny below threshold)
 *    Be able to pass rsi indicator config to SPC, make sure that SPC and CPC return indicator config and signals
 * Add some control to change the candles by timeframe (and candle type)
 * Wrap with the indicator config, but in this case show the used rsi indicators from the result. So show the rsi 3-4 times
 * We need to easily change the rsi indicators and receive/calculate the new signals based on the config
 * Add a horizontal slider to change the xDomain to zoom in
 * Build a way to show the signals or ranges
 * Now we can start playing with the config and see some of it manually and maybe it might give some ideas
 * Maybe after we have this, reconfigure the signals to become the start of reversing rsi indicator, could be promising
 * With the comming of reverse TPTs, we might not have to care too much about how much TPTs we have and the size. But size is still a big question mark though
 * Try with the new signal calculation (start of reverse)
 */
